import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { AlertTriangle, Loader2, CheckCircle2, MessageSquare } from 'lucide-react';
import ChatInterface from './ChatInterface';

interface AnalysisResult {
  property: {
    id: string;
    title: string;
    price: number;
    location: string;
  };
  analysis: {
    risks: string[];
    recommendations: string[];
    roi: number;
  };
}

export default function PropertyAnalyzer() {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [showChat, setShowChat] = useState(false);

  const analyzeProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const propertyData = {
        title: "Apartamento em Leilão",
        price: 150000,
        location: "São Paulo, SP",
        area: 65,
        description: "Apartamento com 2 quartos em excelente localização"
      };

      const { data, error } = await supabase.functions.invoke('analyze-property', {
        body: { propertyData }
      });

      if (error) throw error;
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (showChat) {
    return <ChatInterface onClose={() => setShowChat(false)} />;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={analyzeProperty} className="mb-8">
        <div className="flex gap-4">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Cole o link do imóvel aqui"
            className="flex-1 px-4 py-2 border rounded-lg"
            required
          />
          <button
            type="submit"
            disabled={isLoading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              'Analisar'
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <h3 className="text-xl font-semibold">Análise Concluída</h3>
            </div>
            <button
              onClick={() => setShowChat(true)}
              className="flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <MessageSquare className="h-5 w-5" />
              Consultar Assistente
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Detalhes do Imóvel</h4>
              <p>{result.property.title}</p>
              <p className="text-gray-600">{result.property.location}</p>
              <p className="font-medium">R$ {result.property.price.toLocaleString('pt-BR')}</p>
            </div>

            <div>
              <h4 className="font-medium mb-2">Análise</h4>
              <div className="space-y-2">
                <div>
                  <h5 className="text-sm font-medium text-red-600">Riscos Identificados</h5>
                  <ul className="list-disc list-inside">
                    {result.analysis.risks.map((risk, index) => (
                      <li key={index}>{risk}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-green-600">Recomendações</h5>
                  <ul className="list-disc list-inside">
                    {result.analysis.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="text-sm font-medium text-blue-600">ROI Estimado</h5>
                  <p>{result.analysis.roi}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}